/**
 * Trovva — PDF Form Filler
 *
 * Full-screen modal that opens when an agent clicks "Review" on a
 * generated document. Split pane:
 *   Left:  PDF preview (rendered via <object> / embedded viewer)
 *   Right: Trovva Brain panel + form fields
 *
 * Trovva Brain:
 *   - Analyzes PDF form fields vs deal data
 *   - Presents MCQ questions for every unfilled field
 *   - User selects/types answers → fields update → PDF re-renders
 *
 * Flow: Generate → PdfFormFiller opens → Brain asks MCQs → agent
 *       answers → PDF filled → "Send for Signature" → done
 */
import { useState, useEffect, useCallback } from 'react';
import {
  X, FileText, Check, Eye, Send, Loader2, ChevronRight,
  ChevronLeft, AlertCircle, Brain, Download, Pencil, Shield,
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { theme } from '../../lib/theme';
import { formatDate } from '../../lib/dateFormat';

// ── Field-to-deal mapping: which deal fields answer which PDF form fields ──
// Keys are common NJAR PDF field name patterns, values are deal data paths.
const FIELD_SMART_DEFAULTS = {
  // Buyer info
  buyer_name:       { path: 'contact.first_name + " " + contact.last_name', label: 'Buyer name' },
  purchaser:        { path: 'contact.first_name + " " + contact.last_name', label: 'Buyer/Purchaser' },
  buyer_address:    { path: 'contact.address', label: 'Buyer address' },
  buyer_phone:      { path: 'contact.phone', label: 'Buyer phone' },
  buyer_email:      { path: 'contact.email', label: 'Buyer email' },
  // Property
  property_address: { path: 'property.address', label: 'Property address' },
  property_city:    { path: 'property.city', label: 'City' },
  property_state:   { path: 'property.state', label: 'State' },
  property_zip:     { path: 'property.zip', label: 'ZIP' },
  // Price & dates
  purchase_price:   { path: 'deal.sale_price', label: 'Purchase price', format: 'money' },
  sale_price:       { path: 'deal.sale_price', label: 'Sale price', format: 'money' },
  offer_price:      { path: 'deal.offer_price', label: 'Offer price', format: 'money' },
  closing_date:     { path: 'deal.closing_date', label: 'Closing date', format: 'date' },
  offer_date:       { path: 'deal.offer_date', label: 'Offer date', format: 'date' },
  // Agents & attorneys
  listing_agent:    { path: 'deal.other_agent_name', label: 'Listing agent' },
  listing_broker:   { path: 'deal.other_brokerage', label: 'Listing brokerage' },
  selling_agent:    { path: 'agent.full_name', label: 'Selling agent' },
  selling_broker:   { path: 'org.name', label: 'Selling brokerage' },
  attorney:         { path: 'deal.attorney_name', label: 'Attorney' },
  attorney_phone:   { path: 'deal.attorney_phone', label: 'Attorney phone' },
  // Common NJ fields
  earnest_money:    { path: null, label: 'Earnest money deposit', format: 'money', mcq: true },
  deposit_amount:   { path: null, label: 'Deposit amount', format: 'money', mcq: true },
  financing_days:   { path: null, label: 'Financing contingency period', mcq: true },
  inspection_days:  { path: null, label: 'Inspection contingency period', mcq: true },
  mortgage_amount:  { path: null, label: 'Mortgage amount', format: 'money', mcq: true },
  down_payment:     { path: null, label: 'Down payment', format: 'money', mcq: true },
};

export default function PdfFormFiller({ doc, deal, onClose, onSent, showToast }) {
  const [fields, setFields] = useState([]);        // { name, type, value, autoFilled, label }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [editingField, setEditingField] = useState(null);

  // Separate fields into auto-filled and needs-input
  const autoFilledFields = fields.filter(f => f.autoFilled && f.value);
  const needsInputFields = fields.filter(f => !f.autoFilled || !f.value);

  // ── Load form fields + PDF URL ──────────────────────────────────
  const loadFields = useCallback(async () => {
    if (!doc?.id) return;
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch(`/api/deal-documents?action=get_form_fields&id=${doc.id}`, {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });
      const data = await r.json();
      if (r.ok) {
        setFields(data.fields || []);
        setPdfUrl(data.previewUrl || null);
        setTotalPages(data.pageCount || 1);
      }
    } catch (e) { console.error('Failed to load form fields:', e); }
    finally { setLoading(false); }
  }, [doc?.id]);

  useEffect(() => { loadFields(); }, [loadFields]);

  // ── Save field values back to PDF ───────────────────────────────
  const saveFields = useCallback(async (andSign = false) => {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const fieldValues = {};
      fields.forEach(f => { if (f.value) fieldValues[f.name] = f.value; });

      const r = await fetch('/api/deal-documents?action=fill_form_fields', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: doc.id, fields: fieldValues, flatten: andSign }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Save failed');
      showToast?.(andSign ? 'PDF filled and ready for signature' : 'Draft saved');
      if (andSign && onSent) onSent();
      else {
        // Refresh preview
        if (data.previewUrl) setPdfUrl(data.previewUrl);
      }
    } catch (e) { showToast?.(e.message); }
    finally { setSaving(false); }
  }, [doc?.id, fields, onSent, showToast]);

  // ── Update a field value ────────────────────────────────────────
  const updateField = (fieldName, value) => {
    setFields(prev => prev.map(f =>
      f.name === fieldName ? { ...f, value, autoFilled: true } : f
    ));
  };

  // ── Generate MCQ options for a field ────────────────────────────
  const getMcqOptions = (field) => {
    const salePrice = Number(deal?.sale_price || deal?.offer_price || 0);
    const name = (field.name || '').toLowerCase();

    if (name.includes('earnest') || name.includes('deposit')) {
      const pct1 = Math.round(salePrice * 0.01);
      const pct2 = Math.round(salePrice * 0.02);
      const pct3 = Math.round(salePrice * 0.03);
      return [
        ...(pct1 > 0 ? [{ label: `$${pct1.toLocaleString()} (1% of sale price)`, value: String(pct1) }] : []),
        ...(pct2 > 0 ? [{ label: `$${pct2.toLocaleString()} (2% of sale price)`, value: String(pct2) }] : []),
        ...(pct3 > 0 ? [{ label: `$${pct3.toLocaleString()} (3% of sale price)`, value: String(pct3) }] : []),
      ];
    }
    if (name.includes('financing') || name.includes('mortgage_contingency')) {
      return [
        { label: '7 days (NJ standard)', value: '7' },
        { label: '10 days', value: '10' },
        { label: '14 days', value: '14' },
        { label: '21 days', value: '21' },
      ];
    }
    if (name.includes('inspection')) {
      return [
        { label: '7 days (NJ standard)', value: '7' },
        { label: '10 days', value: '10' },
        { label: '14 days', value: '14' },
      ];
    }
    if (name.includes('mortgage_amount') || name.includes('loan_amount')) {
      const dp20 = Math.round(salePrice * 0.80);
      const dp10 = Math.round(salePrice * 0.90);
      const dp5 = Math.round(salePrice * 0.95);
      return [
        ...(dp20 > 0 ? [{ label: `$${dp20.toLocaleString()} (80% LTV — 20% down)`, value: String(dp20) }] : []),
        ...(dp10 > 0 ? [{ label: `$${dp10.toLocaleString()} (90% LTV — 10% down)`, value: String(dp10) }] : []),
        ...(dp5 > 0 ? [{ label: `$${dp5.toLocaleString()} (95% LTV — 5% down)`, value: String(dp5) }] : []),
      ];
    }
    if (name.includes('down_payment')) {
      const dp20 = Math.round(salePrice * 0.20);
      const dp10 = Math.round(salePrice * 0.10);
      const dp5 = Math.round(salePrice * 0.05);
      return [
        ...(dp20 > 0 ? [{ label: `$${dp20.toLocaleString()} (20%)`, value: String(dp20) }] : []),
        ...(dp10 > 0 ? [{ label: `$${dp10.toLocaleString()} (10%)`, value: String(dp10) }] : []),
        ...(dp5 > 0 ? [{ label: `$${dp5.toLocaleString()} (5%)`, value: String(dp5) }] : []),
      ];
    }
    return [];
  };

  if (!doc) return null;

  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 500,
      display: 'flex',
    }}>
      <div onClick={e => e.stopPropagation()} style={{
        width: '100%', height: '100%',
        background: theme.colors.card, overflow: 'hidden',
        display: 'flex', flexDirection: 'column',
      }}>
        {/* ── Header ──────────────────────────────────────────── */}
        <div style={{
          padding: '10px 18px', borderBottom: `1px solid ${theme.colors.border}`,
          display: 'flex', alignItems: 'center', gap: 10, flexShrink: 0,
          background: theme.colors.card,
        }}>
          <FileText size={16} color={theme.colors.primary} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: theme.colors.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {doc.name}
            </div>
            <div style={{ fontSize: 11, color: theme.colors.textLight }}>
              {deal?.property_address || 'Deal document'} · Review and fill
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => saveFields(false)} disabled={saving} style={{
              padding: '6px 12px', borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: 'transparent', fontSize: 11, fontWeight: 600, cursor: 'pointer',
              color: theme.colors.text, display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              {saving ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={11} />} Save draft
            </button>
            <button onClick={() => saveFields(true)} disabled={saving} style={{
              padding: '6px 14px', borderRadius: 8, border: 'none',
              background: '#8B5CF6', color: 'white', fontSize: 11, fontWeight: 700,
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 4,
            }}>
              <Send size={11} /> Send for signature
            </button>
            <button onClick={onClose} style={{
              width: 30, height: 30, borderRadius: 8, border: `1px solid ${theme.colors.border}`,
              background: 'transparent', cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight,
            }}><X size={14} /></button>
          </div>
        </div>

        {/* ── Split pane ──────────────────────────────────────── */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>

          {/* ── Left: PDF Preview ─────────────────────────────── */}
          <div style={{
            flex: 1, display: 'flex', flexDirection: 'column',
            borderRight: `1px solid ${theme.colors.border}`, background: '#F3F4F6',
            minWidth: 0,
          }}>
            <div style={{ flex: 1, padding: 4 }}>
              {pdfUrl ? (
                <object data={`${pdfUrl}#navpanes=0&zoom=page-width`} type="application/pdf" style={{
                  width: '100%', height: '100%', borderRadius: 6, border: 'none',
                }}>
                  <div style={{ padding: 40, textAlign: 'center', color: theme.colors.textLight }}>
                    <FileText size={32} style={{ marginBottom: 8, opacity: 0.4 }} />
                    <div>PDF preview not supported in this browser.</div>
                    <a href={pdfUrl} target="_blank" rel="noopener" style={{ color: theme.colors.primary, fontSize: 12 }}>Open in new tab</a>
                  </div>
                </object>
              ) : loading ? (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight }}>
                  <Loader2 size={18} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} /> Loading PDF…
                </div>
              ) : (
                <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight }}>
                  <FileText size={32} style={{ marginRight: 8, opacity: 0.3 }} /> Preview will appear after fields are loaded
                </div>
              )}
            </div>
          </div>

          {/* ── Right: Trovva Brain + Form Fields ─────────────── */}
          <div style={{ width: 340, flexShrink: 0, display: 'flex', flexDirection: 'column', minHeight: 0 }}>

            {loading ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: theme.colors.textLight }}>
                <Loader2 size={16} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} /> Analyzing form fields…
              </div>
            ) : (
              <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>

                {/* ── Trovva Brain MCQ Section ────────────────── */}
                {needsInputFields.length > 0 && (
                  <div style={{ padding: '14px 16px', borderBottom: `1px solid ${theme.colors.border}`, background: '#8B5CF605' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                      <div style={{
                        width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                        background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Brain size={12} color="white" />
                      </div>
                      <span style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Trovva Brain</span>
                      <span style={{ fontSize: 11, color: theme.colors.textLight, marginLeft: 'auto' }}>
                        {needsInputFields.length} field{needsInputFields.length !== 1 ? 's' : ''} need{needsInputFields.length === 1 ? 's' : ''} your input
                      </span>
                    </div>
                    <div style={{ fontSize: 12, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 12 }}>
                      I auto-filled {autoFilledFields.length} field{autoFilledFields.length !== 1 ? 's' : ''} from your deal data.
                      {needsInputFields.length > 0 && ` Please answer the following to complete the form:`}
                    </div>

                    {/* MCQ cards for each unfilled field */}
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {needsInputFields.map(field => {
                        const options = getMcqOptions(field);
                        return (
                          <McqCard
                            key={field.name}
                            field={field}
                            options={options}
                            onSelect={(value) => updateField(field.name, value)}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── All clear message ──────────────────────── */}
                {needsInputFields.length === 0 && fields.length > 0 && (
                  <div style={{
                    padding: '14px 16px', borderBottom: `1px solid ${theme.colors.border}`,
                    background: '#10B98108', display: 'flex', gap: 10, alignItems: 'center',
                  }}>
                    <div style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: theme.colors.success, display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Check size={12} color="white" />
                    </div>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>All fields filled</div>
                      <div style={{ fontSize: 12, color: theme.colors.textLight }}>
                        Review the values below, then click "Send for signature" when ready.
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Auto-filled fields — review & edit ─────── */}
                {autoFilledFields.length > 0 && (
                  <div style={{ padding: '14px 16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
                      <div style={{ fontSize: 11, fontWeight: 700, color: theme.colors.text, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Auto-filled fields ({autoFilledFields.length})
                      </div>
                    </div>
                    <div style={{
                      padding: '8px 10px', borderRadius: 8, marginBottom: 10,
                      background: '#FEF3C7', border: '1px solid #F59E0B30',
                      fontSize: 11, color: '#92400E', lineHeight: 1.5,
                      display: 'flex', alignItems: 'flex-start', gap: 6,
                    }}>
                      <AlertCircle size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                      <span>
                        These values were filled from your deal data. <strong>Please review each one</strong> — click any field to edit if something looks wrong.
                      </span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {autoFilledFields.map(field => (
                        <AutoFilledRow
                          key={field.name}
                          field={field}
                          editing={editingField === field.name}
                          onEdit={() => setEditingField(field.name)}
                          onSave={(val) => { updateField(field.name, val); setEditingField(null); }}
                          onCancel={() => setEditingField(null)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                {/* ── Document Verification ────────────────── */}
                <VerificationAction doc={doc} showToast={showToast} onVerified={() => { /* parent refreshes on close */ }} />

                {/* No form fields — show review-focused panel */}
                {fields.length === 0 && !loading && (
                  <div style={{ padding: '16px 16px' }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14,
                      padding: '10px 12px', borderRadius: 8,
                      background: '#8B5CF608', border: '1px solid #8B5CF618',
                    }}>
                      <div style={{
                        width: 24, height: 24, borderRadius: '50%', flexShrink: 0,
                        background: '#8B5CF6', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      }}>
                        <Brain size={13} color="white" />
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.text }}>Review this document</div>
                        <div style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.4 }}>
                          Read the PDF on the left. When satisfied, verify below and send for signature.
                        </div>
                      </div>
                    </div>

                    {/* Deal context summary */}
                    {deal && (
                      <div style={{
                        padding: '10px 12px', borderRadius: 8, marginBottom: 14,
                        background: theme.colors.bg, border: `1px solid ${theme.colors.border}`,
                      }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: theme.colors.textLight, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6 }}>
                          Deal context
                        </div>
                        {deal.property_address && (
                          <div style={{ fontSize: 12, color: theme.colors.text, marginBottom: 3 }}>
                            <strong>Property:</strong> {deal.property_address}
                          </div>
                        )}
                        {(deal.sale_price || deal.offer_price) && (
                          <div style={{ fontSize: 12, color: theme.colors.text, marginBottom: 3 }}>
                            <strong>Price:</strong> ${Number(deal.sale_price || deal.offer_price).toLocaleString()}
                          </div>
                        )}
                        {deal.closing_date && (
                          <div style={{ fontSize: 12, color: theme.colors.text, marginBottom: 3 }}>
                            <strong>Closing:</strong> {formatDate(deal.closing_date)}
                          </div>
                        )}
                        {deal.status && (
                          <div style={{ fontSize: 12, color: theme.colors.text }}>
                            <strong>Status:</strong> <span style={{ textTransform: 'capitalize' }}>{deal.status.replace(/_/g, ' ')}</span>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <style>{`@keyframes spin { from{transform:rotate(0)} to{transform:rotate(360deg)} }`}</style>
      </div>
    </div>
  );
}

// ── MCQ Card ─────────────────────────────────────────────────────
function McqCard({ field, options, onSelect }) {
  const [customValue, setCustomValue] = useState('');
  const [selected, setSelected] = useState(field.value || null);

  const handleSelect = (value) => {
    setSelected(value);
    onSelect(value);
  };

  return (
    <div style={{
      background: theme.colors.card, borderRadius: 10, padding: '10px 12px',
      border: `1px solid ${selected ? theme.colors.success + '40' : theme.colors.border}`,
    }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: theme.colors.text, marginBottom: 6 }}>
        {field.label || field.name.replace(/_/g, ' ')}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {options.map((opt, i) => {
          const isSelected = selected === opt.value;
          return (
            <label key={i} onClick={() => handleSelect(opt.value)} style={{
              display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
              padding: '5px 8px', borderRadius: 6,
              background: isSelected ? `${theme.colors.primary}08` : 'transparent',
              border: `1px solid ${isSelected ? theme.colors.primary + '40' : 'transparent'}`,
            }}>
              <span style={{
                width: 14, height: 14, borderRadius: 7, flexShrink: 0,
                border: `2px solid ${isSelected ? theme.colors.primary : theme.colors.border}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                {isSelected && <span style={{ width: 6, height: 6, borderRadius: 3, background: theme.colors.primary }} />}
              </span>
              <span style={{ fontSize: 12, color: theme.colors.text }}>{opt.label}</span>
            </label>
          );
        })}

        {/* Custom input option */}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 7, cursor: 'pointer',
          padding: '5px 8px', borderRadius: 6,
          background: selected && !options.find(o => o.value === selected) ? `${theme.colors.primary}08` : 'transparent',
          border: `1px solid ${selected && !options.find(o => o.value === selected) ? theme.colors.primary + '40' : 'transparent'}`,
        }}>
          <span style={{
            width: 14, height: 14, borderRadius: 7, flexShrink: 0,
            border: `2px solid ${selected && !options.find(o => o.value === selected) ? theme.colors.primary : theme.colors.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            {selected && !options.find(o => o.value === selected) && (
              <span style={{ width: 6, height: 6, borderRadius: 3, background: theme.colors.primary }} />
            )}
          </span>
          <span style={{ fontSize: 12, color: theme.colors.textLight }}>Custom:</span>
          <input
            value={customValue}
            onChange={e => setCustomValue(e.target.value)}
            onBlur={() => { if (customValue.trim()) handleSelect(customValue.trim()); }}
            onKeyDown={e => { if (e.key === 'Enter' && customValue.trim()) handleSelect(customValue.trim()); }}
            placeholder={field.format === 'money' ? '$' : 'Type here…'}
            style={{
              flex: 1, padding: '3px 6px', borderRadius: 4, fontSize: 11,
              border: `1px solid ${theme.colors.border}`, color: theme.colors.text,
              background: theme.colors.card, minWidth: 60,
            }}
            onFocus={() => {
              if (options.find(o => o.value === selected)) setSelected(null);
            }}
          />
        </label>
      </div>
    </div>
  );
}

// ── Auto-filled field row ────────────────────────────────────────
function AutoFilledRow({ field, editing, onEdit, onSave, onCancel }) {
  const [val, setVal] = useState(field.value || '');

  if (editing) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px',
        borderRadius: 6, background: `${theme.colors.primary}06`,
        border: `1px solid ${theme.colors.primary}25`,
      }}>
        <span style={{ fontSize: 11, color: theme.colors.primary, fontWeight: 600, minWidth: 100, flexShrink: 0 }}>
          {field.label || field.name.replace(/_/g, ' ')}
        </span>
        <input
          autoFocus
          value={val}
          onChange={e => setVal(e.target.value)}
          onBlur={() => onSave(val)}
          onKeyDown={e => {
            if (e.key === 'Enter') onSave(val);
            if (e.key === 'Escape') onCancel();
          }}
          style={{
            flex: 1, padding: '3px 6px', borderRadius: 4, fontSize: 12,
            border: `1px solid ${theme.colors.primary}40`, color: theme.colors.text,
            background: theme.colors.card, fontWeight: 500,
          }}
        />
      </div>
    );
  }

  return (
    <button onClick={onEdit} style={{
      display: 'flex', alignItems: 'center', gap: 6, padding: '5px 8px', width: '100%',
      borderRadius: 6, background: `${theme.colors.success}06`, textAlign: 'left',
      border: `1px solid ${theme.colors.success}15`, cursor: 'pointer',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = `${theme.colors.success}12`; }}
    onMouseLeave={e => { e.currentTarget.style.background = `${theme.colors.success}06`; }}
    >
      <span style={{ fontSize: 11, color: theme.colors.success, fontWeight: 600, minWidth: 100, flexShrink: 0 }}>
        {field.label || field.name.replace(/_/g, ' ')}
      </span>
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: theme.colors.text }}>
        {field.value}
      </span>
      <Pencil size={10} color={theme.colors.textLight} style={{ flexShrink: 0, opacity: 0.5 }} />
    </button>
  );
}

// ── Document Verification Action ─────────────────────────────────
// Agent clicks "I have reviewed this document" → creates verification record.
// Broker clicks "I verify this document" → creates broker verification.
// Both are timestamped for the audit trail.
function VerificationAction({ doc, showToast, onVerified }) {
  const [verifying, setVerifying] = useState(false);
  const [notes, setNotes] = useState('');
  const [verified, setVerified] = useState(false);
  const [verifiedAction, setVerifiedAction] = useState(null);

  const agentOk = !!doc?.agent_verified_at;
  const brokerOk = !!doc?.broker_verified_at;

  const handleVerify = async (action) => {
    setVerifying(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const r = await fetch('/api/deal-verify?action=verify', {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_id: doc.id, action, notes: notes.trim() || null }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Verification failed');
      setVerified(true);
      setVerifiedAction(action);
      showToast?.(action === 'approved'
        ? 'Document verified — logged to audit trail'
        : 'Review recorded — logged to audit trail', 'success');
      // Notify parent to refresh doc list (pipeline will update)
      onVerified?.();
    } catch (e) {
      console.error('Verification error:', e);
      showToast?.(e.message, 'error');
    }
    finally { setVerifying(false); }
  };

  if (verified || (agentOk && brokerOk)) {
    const fullyDone = (agentOk && brokerOk) || (verified && verifiedAction === 'approved' && agentOk);
    return (
      <div style={{
        padding: '14px 16px', borderBottom: `1px solid ${theme.colors.border}`,
        background: '#10B98108',
      }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: fullyDone ? 8 : 0 }}>
          <div style={{
            width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
            background: theme.colors.success, display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}><Check size={12} color="white" /></div>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: theme.colors.success }}>
              {verified ? 'Your verification has been recorded' : 'Fully verified by agent and broker'}
            </div>
            {verified && verifiedAction === 'reviewed' && (
              <div style={{ fontSize: 11, color: theme.colors.textLight, marginTop: 2 }}>
                Agent review complete. Broker verification is the next step.
              </div>
            )}
          </div>
        </div>
        {fullyDone && (
          <div style={{
            padding: '8px 10px', borderRadius: 6, marginTop: 4,
            background: '#8B5CF608', border: '1px solid #8B5CF618',
            fontSize: 11, color: '#8B5CF6', fontWeight: 600, lineHeight: 1.5,
          }}>
            This document is ready to send for signature. Close this viewer, select it in the pipeline, and click "Bundle & Send."
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{
      padding: '14px 16px', borderBottom: `1px solid ${theme.colors.border}`,
      background: '#F59E0B06',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Shield size={14} color="#F59E0B" />
        <span style={{ fontSize: 12, fontWeight: 700, color: theme.colors.text }}>Document Verification</span>
      </div>

      <div style={{ fontSize: 11, color: theme.colors.textLight, lineHeight: 1.5, marginBottom: 10 }}>
        After reviewing this document, confirm your verification below.
        This is logged to the audit trail with your name, role, and timestamp.
      </div>

      {/* Status indicators */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
          <div style={{
            width: 10, height: 10, borderRadius: 5,
            background: agentOk ? theme.colors.success : theme.colors.border,
          }} />
          <span style={{ color: agentOk ? theme.colors.success : theme.colors.textLight }}>
            Agent {agentOk ? 'reviewed' : 'pending'}
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 11 }}>
          <div style={{
            width: 10, height: 10, borderRadius: 5,
            background: brokerOk ? theme.colors.success : theme.colors.border,
          }} />
          <span style={{ color: brokerOk ? theme.colors.success : theme.colors.textLight }}>
            Broker {brokerOk ? 'verified' : 'pending'}
          </span>
        </div>
      </div>

      <textarea
        value={notes}
        onChange={e => setNotes(e.target.value)}
        placeholder="Optional notes about your review…"
        rows={2}
        style={{
          width: '100%', padding: '6px 8px', borderRadius: 6, fontSize: 11,
          border: `1px solid ${theme.colors.border}`, color: theme.colors.text,
          background: theme.colors.card, resize: 'vertical', fontFamily: 'inherit',
          boxSizing: 'border-box', marginBottom: 8,
        }}
      />

      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => handleVerify('reviewed')} disabled={verifying} style={{
          flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
          background: theme.colors.primary, color: 'white',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          opacity: verifying ? 0.6 : 1,
        }}>
          {verifying ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Eye size={11} />}
          I have reviewed this document
        </button>
        <button onClick={() => handleVerify('approved')} disabled={verifying} style={{
          flex: 1, padding: '8px 12px', borderRadius: 8, border: 'none',
          background: theme.colors.success, color: 'white',
          fontSize: 11, fontWeight: 700, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
          opacity: verifying ? 0.6 : 1,
        }}>
          {verifying ? <Loader2 size={11} style={{ animation: 'spin 1s linear infinite' }} /> : <Check size={11} />}
          I verify this document
        </button>
      </div>
    </div>
  );
}
